import * as Tone from 'tone';
import type { KitId, Lane, MusicEvent, Seconds } from '../core/types';
import { beatToSeconds } from './plan';

type OscillatorKind = 'sine' | 'triangle' | 'sawtooth' | 'square';
type NoiseKind = 'white' | 'pink' | 'brown';

const BAND_GAIN_MIN = 0.3;
const BAND_GAIN_MAX = 0.62;
const INTENSITY_RAMP_SECONDS = 0.1;
const STOP_FADE_SECONDS = 0.08;
const MIN_NOTE_SECONDS = 0.04;
const PERC_ATTACK_SECONDS = 0.001;
const PERC_RELEASE_SECONDS = 0.02;
const MAX_POLYPHONY = 8;
const SIDECHAIN_DEPTH = 0.45;
const SIDECHAIN_RELEASE_SECONDS = 0.18;

const LANE_VELOCITY: Record<Lane, number> = {
  kick: 1,
  snare: 0.9,
  hat: 0.7,
  bass: 0.95,
  chord: 0.65,
  lead: 0.75,
  sub: 1,
};

interface KickSpec {
  readonly oscillator: OscillatorKind;
  readonly baseHz: number;
  readonly pitchDecay: number;
  readonly octaves: number;
  readonly decaySeconds: number;
  readonly hitSeconds: number;
}

interface SnareSpec {
  readonly noise: NoiseKind;
  readonly decaySeconds: number;
  readonly releaseSeconds: number;
  readonly hitSeconds: number;
}

interface HatSpec {
  readonly noise: NoiseKind;
  readonly decaySeconds: number;
  readonly hitSeconds: number;
  readonly highpassHz: number;
  readonly highpassQ: number;
}

interface BassSpec {
  readonly oscillator: OscillatorKind;
  readonly baseHz: number;
  readonly portamento: number;
  readonly attackSeconds: number;
  readonly decaySeconds: number;
  readonly sustain: number;
  readonly releaseSeconds: number;
  readonly filterBaseHz: number;
  readonly filterOctaves: number;
  readonly filterQ: number;
}

interface VoiceSpec {
  readonly oscillator: OscillatorKind;
  readonly baseHz: number;
  readonly attackSeconds: number;
  readonly decaySeconds: number;
  readonly sustain: number;
  readonly releaseSeconds: number;
}

interface BandSpec {
  readonly kick: KickSpec;
  readonly snare: SnareSpec;
  readonly hat: HatSpec;
  readonly bass: BassSpec;
  readonly chord: VoiceSpec;
  readonly lead: VoiceSpec;
  readonly gains: Record<Lane, number>;
  readonly filterMinHz: number;
  readonly filterMaxHz: number;
  readonly filterQ: number;
}

const BANDS: Record<KitId, BandSpec> = {
  edm: {
    kick: {
      oscillator: 'sine',
      baseHz: 50,
      pitchDecay: 0.035,
      octaves: 5,
      decaySeconds: 0.3,
      hitSeconds: 0.18,
    },
    snare: { noise: 'white', decaySeconds: 0.14, releaseSeconds: 0.05, hitSeconds: 0.09 },
    hat: {
      noise: 'white',
      decaySeconds: 0.026,
      hitSeconds: 0.02,
      highpassHz: 8200,
      highpassQ: 1,
    },
    bass: {
      oscillator: 'sawtooth',
      baseHz: 55,
      portamento: 0.02,
      attackSeconds: 0.006,
      decaySeconds: 0.18,
      sustain: 0.55,
      releaseSeconds: 0.12,
      filterBaseHz: 130,
      filterOctaves: 2.6,
      filterQ: 2,
    },
    chord: {
      oscillator: 'sawtooth',
      baseHz: 220,
      attackSeconds: 0.02,
      decaySeconds: 0.4,
      sustain: 0.32,
      releaseSeconds: 0.4,
    },
    lead: {
      oscillator: 'square',
      baseHz: 440,
      attackSeconds: 0.005,
      decaySeconds: 0.2,
      sustain: 0.24,
      releaseSeconds: 0.16,
    },
    gains: { kick: 0.52, snare: 0.34, hat: 0.16, bass: 0.38, chord: 0.2, lead: 0.22, sub: 0.3 },
    filterMinHz: 600,
    filterMaxHz: 14000,
    filterQ: 0.8,
  },
  phonk: {
    kick: {
      oscillator: 'sine',
      baseHz: 41,
      pitchDecay: 0.1,
      octaves: 8,
      decaySeconds: 0.62,
      hitSeconds: 0.4,
    },
    snare: { noise: 'pink', decaySeconds: 0.22, releaseSeconds: 0.12, hitSeconds: 0.14 },
    hat: {
      noise: 'white',
      decaySeconds: 0.018,
      hitSeconds: 0.014,
      highpassHz: 9600,
      highpassQ: 1.4,
    },
    bass: {
      oscillator: 'triangle',
      baseHz: 41.2,
      portamento: 0.09,
      attackSeconds: 0.01,
      decaySeconds: 0.5,
      sustain: 0.7,
      releaseSeconds: 0.3,
      filterBaseHz: 90,
      filterOctaves: 1.8,
      filterQ: 1.2,
    },
    chord: {
      oscillator: 'triangle',
      baseHz: 164.8,
      attackSeconds: 0.05,
      decaySeconds: 0.7,
      sustain: 0.22,
      releaseSeconds: 0.7,
    },
    lead: {
      oscillator: 'sine',
      baseHz: 659.3,
      attackSeconds: 0.004,
      decaySeconds: 0.5,
      sustain: 0.06,
      releaseSeconds: 0.5,
    },
    gains: { kick: 0.56, snare: 0.3, hat: 0.13, bass: 0.42, chord: 0.18, lead: 0.2, sub: 0.34 },
    filterMinHz: 420,
    filterMaxHz: 9000,
    filterQ: 1.3,
  },
  funk: {
    kick: {
      oscillator: 'triangle',
      baseHz: 62,
      pitchDecay: 0.022,
      octaves: 4,
      decaySeconds: 0.2,
      hitSeconds: 0.12,
    },
    snare: { noise: 'white', decaySeconds: 0.1, releaseSeconds: 0.03, hitSeconds: 0.07 },
    hat: {
      noise: 'pink',
      decaySeconds: 0.034,
      hitSeconds: 0.026,
      highpassHz: 6800,
      highpassQ: 0.9,
    },
    bass: {
      oscillator: 'square',
      baseHz: 49,
      portamento: 0.012,
      attackSeconds: 0.004,
      decaySeconds: 0.14,
      sustain: 0.4,
      releaseSeconds: 0.08,
      filterBaseHz: 160,
      filterOctaves: 2.2,
      filterQ: 3,
    },
    chord: {
      oscillator: 'square',
      baseHz: 196,
      attackSeconds: 0.008,
      decaySeconds: 0.26,
      sustain: 0.18,
      releaseSeconds: 0.22,
    },
    lead: {
      oscillator: 'square',
      baseHz: 392,
      attackSeconds: 0.003,
      decaySeconds: 0.16,
      sustain: 0.2,
      releaseSeconds: 0.12,
    },
    gains: { kick: 0.5, snare: 0.36, hat: 0.2, bass: 0.34, chord: 0.18, lead: 0.24, sub: 0.32 },
    filterMinHz: 700,
    filterMaxHz: 12000,
    filterQ: 0.7,
  },
};

const SUB_PORTAMENTO = 0.05;
const SUB_ATTACK_SECONDS = 0.012;
const SUB_DECAY_SECONDS = 0.4;
const SUB_SUSTAIN = 0.75;
const SUB_RELEASE_SECONDS = 0.25;
const SUB_FILTER_HZ = 120;

export interface Band {
  play(event: MusicEvent, time: Seconds, bpm: number): void;
  setIntensity(level: number, when?: Seconds): void;
  stop(when?: Seconds): void;
  dispose(): void;
  readonly output: Tone.Gain;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export function createBand(kit: KitId, destination: Tone.InputNode): Band {
  const spec = BANDS[kit];

  const output = new Tone.Gain(BAND_GAIN_MIN);
  output.connect(destination);

  const filter = new Tone.Filter({
    type: 'lowpass',
    frequency: spec.filterMinHz,
    Q: spec.filterQ,
    rolloff: -12,
  }).connect(output);

  const duck = new Tone.Gain(1).connect(filter);
  const leadBus = new Tone.Gain(1).connect(output);

  const gains: Record<Lane, Tone.Gain> = {
    kick: new Tone.Gain(spec.gains.kick).connect(filter),
    snare: new Tone.Gain(spec.gains.snare).connect(filter),
    hat: new Tone.Gain(spec.gains.hat).connect(filter),
    bass: new Tone.Gain(spec.gains.bass).connect(duck),
    chord: new Tone.Gain(spec.gains.chord).connect(duck),
    lead: new Tone.Gain(spec.gains.lead).connect(leadBus),
    sub: new Tone.Gain(spec.gains.sub).connect(duck),
  };

  const kick = new Tone.MembraneSynth({
    oscillator: { type: spec.kick.oscillator },
    pitchDecay: spec.kick.pitchDecay,
    octaves: spec.kick.octaves,
    envelope: {
      attack: PERC_ATTACK_SECONDS,
      decay: spec.kick.decaySeconds,
      sustain: 0,
      release: PERC_RELEASE_SECONDS,
    },
  }).connect(gains.kick);

  const snare = new Tone.NoiseSynth({
    noise: { type: spec.snare.noise },
    envelope: {
      attack: PERC_ATTACK_SECONDS,
      decay: spec.snare.decaySeconds,
      sustain: 0,
      release: spec.snare.releaseSeconds,
    },
  }).connect(gains.snare);

  const hatFilter = new Tone.Filter({
    type: 'highpass',
    frequency: spec.hat.highpassHz,
    Q: spec.hat.highpassQ,
    rolloff: -12,
  }).connect(gains.hat);

  const hat = new Tone.NoiseSynth({
    noise: { type: spec.hat.noise },
    envelope: {
      attack: PERC_ATTACK_SECONDS,
      decay: spec.hat.decaySeconds,
      sustain: 0,
      release: PERC_RELEASE_SECONDS,
    },
  }).connect(hatFilter);

  const bass = new Tone.MonoSynth({
    portamento: spec.bass.portamento,
    oscillator: { type: spec.bass.oscillator },
    envelope: {
      attack: spec.bass.attackSeconds,
      decay: spec.bass.decaySeconds,
      sustain: spec.bass.sustain,
      release: spec.bass.releaseSeconds,
    },
    filter: { type: 'lowpass', Q: spec.bass.filterQ, rolloff: -24 },
    filterEnvelope: {
      attack: 0.01,
      decay: 0.2,
      sustain: 0.4,
      release: 0.25,
      baseFrequency: spec.bass.filterBaseHz,
      octaves: spec.bass.filterOctaves,
    },
  }).connect(gains.bass);

  const chord = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: spec.chord.oscillator },
    envelope: {
      attack: spec.chord.attackSeconds,
      decay: spec.chord.decaySeconds,
      sustain: spec.chord.sustain,
      release: spec.chord.releaseSeconds,
    },
  }).connect(gains.chord);
  chord.maxPolyphony = MAX_POLYPHONY;

  const lead = new Tone.Synth({
    oscillator: { type: spec.lead.oscillator },
    envelope: {
      attack: spec.lead.attackSeconds,
      decay: spec.lead.decaySeconds,
      sustain: spec.lead.sustain,
      release: spec.lead.releaseSeconds,
    },
  }).connect(gains.lead);

  const sub = new Tone.MonoSynth({
    portamento: SUB_PORTAMENTO,
    oscillator: { type: 'sine' },
    envelope: {
      attack: SUB_ATTACK_SECONDS,
      decay: SUB_DECAY_SECONDS,
      sustain: SUB_SUSTAIN,
      release: SUB_RELEASE_SECONDS,
    },
    filter: { type: 'lowpass', Q: 0.6, rolloff: -24 },
    filterEnvelope: {
      attack: 0.01,
      decay: 0.3,
      sustain: 0.6,
      release: 0.4,
      baseFrequency: SUB_FILTER_HZ,
      octaves: 1.2,
    },
  }).connect(gains.sub);

  let intensity = 0;
  let silenced = false;

  function mixCutoff(): number {
    return spec.filterMinHz * Math.pow(spec.filterMaxHz / spec.filterMinHz, intensity);
  }

  function mixGain(): number {
    return BAND_GAIN_MIN + (BAND_GAIN_MAX - BAND_GAIN_MIN) * intensity;
  }

  function applyMix(when: Seconds, ramp: Seconds): void {
    filter.frequency.cancelScheduledValues(when);
    filter.frequency.setValueAtTime(filter.frequency.getValueAtTime(when), when);
    filter.frequency.exponentialRampToValueAtTime(mixCutoff(), when + ramp);
    output.gain.cancelScheduledValues(when);
    output.gain.setValueAtTime(output.gain.getValueAtTime(when), when);
    output.gain.linearRampToValueAtTime(mixGain(), when + ramp);
  }

  function noteSeconds(durationBeats: number, bpm: number): Seconds {
    const seconds = beatToSeconds(durationBeats, bpm);
    return seconds > MIN_NOTE_SECONDS ? seconds : MIN_NOTE_SECONDS;
  }

  function applyIntensity(level: number, when: Seconds): void {
    intensity = clamp01(level);
    applyMix(when, INTENSITY_RAMP_SECONDS);
  }

  function duckKick(level: number, time: Seconds, bpm: number): void {
    const target = Math.min(1, Math.max(0.2, 1 - SIDECHAIN_DEPTH * clamp01(level)));
    const release = Math.min(SIDECHAIN_RELEASE_SECONDS, 30 / bpm);
    duck.gain.cancelScheduledValues(time);
    duck.gain.setValueAtTime(target, time);
    duck.gain.linearRampToValueAtTime(1, time + release);
  }

  function trigger(event: MusicEvent, time: Seconds, bpm: number): void {
    const velocity = clamp01(event.level) * LANE_VELOCITY[event.lane];
    if (velocity <= 0) return;
    switch (event.lane) {
      case 'kick':
        kick.triggerAttackRelease(
          event.pitchHz ?? spec.kick.baseHz,
          spec.kick.hitSeconds,
          time,
          velocity,
        );
        return;
      case 'snare':
        snare.triggerAttackRelease(spec.snare.hitSeconds, time, velocity);
        return;
      case 'hat':
        hat.triggerAttackRelease(spec.hat.hitSeconds, time, velocity);
        return;
      case 'bass':
        bass.triggerAttackRelease(
          event.pitchHz ?? spec.bass.baseHz,
          noteSeconds(event.durationBeats, bpm),
          time,
          velocity,
        );
        return;
      case 'chord': {
        const notes = event.chordHz === undefined ? [spec.chord.baseHz] : [...event.chordHz];
        if (notes.length === 0) return;
        chord.triggerAttackRelease(notes, noteSeconds(event.durationBeats, bpm), time, velocity);
        return;
      }
      case 'lead':
        lead.triggerAttackRelease(
          event.pitchHz ?? spec.lead.baseHz,
          noteSeconds(event.durationBeats, bpm),
          time,
          velocity,
        );
        return;
      case 'sub':
        sub.triggerAttackRelease(
          event.pitchHz ?? spec.bass.baseHz / 2,
          noteSeconds(event.durationBeats, bpm),
          time,
          velocity,
        );
        return;
    }
  }

  return {
    play(event: MusicEvent, time: Seconds, bpm: number): void {
      if (silenced) {
        silenced = false;
        applyIntensity(intensity, time);
      }
      if (event.lane === 'kick') {
        duckKick(event.level, time, bpm);
      }
      trigger(event, time, bpm);
    },

    setIntensity(level: number, when?: Seconds): void {
      applyIntensity(level, when ?? Tone.immediate());
    },

    stop(when?: Seconds): void {
      const time = when ?? Tone.immediate();
      chord.releaseAll(time);
      bass.triggerRelease(time);
      lead.triggerRelease(time);
      sub.triggerRelease(time);
      filter.frequency.cancelScheduledValues(time);
      filter.frequency.setValueAtTime(spec.filterMinHz, time);
      output.gain.cancelScheduledValues(time);
      output.gain.setValueAtTime(output.gain.getValueAtTime(time), time);
      output.gain.linearRampToValueAtTime(0, time + STOP_FADE_SECONDS);
      silenced = true;
    },

    dispose(): void {
      kick.dispose();
      snare.dispose();
      hat.dispose();
      hatFilter.dispose();
      bass.dispose();
      chord.dispose();
      lead.dispose();
      sub.dispose();
      gains.kick.dispose();
      gains.snare.dispose();
      gains.hat.dispose();
      gains.bass.dispose();
      gains.chord.dispose();
      gains.sub.dispose();
      gains.lead.dispose();
      leadBus.dispose();
      duck.dispose();
      filter.dispose();
      output.dispose();
    },

    output,
  };
}
